// ────────── PCNModal.jsx ────────────────────────────────────────────────
import React, { useState, useEffect } from 'react';
import {
  Modal, Box, Button, FormControl, FormLabel, RadioGroup,
  FormControlLabel, Radio, Alert, AlertTitle, Collapse, IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import DocumentService from '../../Services/documents.service';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import PCNDocumentationModal from './PCNDocumentationModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
/**
 * PCN Storno‑/Date‑picker modal
 *
 * All state is fully controlled by the parent through props
 * so the component stays 100 % dumb / reusable.
 */
export default function PCNModal({
  /* visibility */
  open,
  onClose,

  /* layout */
  direction = 'ltr',

  /* date range vs single date */
  isDateRange,
  setIsDateRange,
  date, setDate,
  startDate, setStartDate,
  endDate, setEndDate,
  CustomMonthDatePicker,

  /* storno flag */
  pcnIncludeStornoChecks,
  setPcnIncludeStornoChecks,

  /* async actions */
  pcnChecksLoading,
  handlePCNChecks,
  handlePCNDownload,

  /* i18n */
  t,

  /* client and token for reference check */
  caseId: propCaseId,
  token: propToken,
  viewDocumentDetails,
}) {
  const { menuProps } = useHamburgerMenu();
  const [referenceContinuityIssues, setReferenceContinuityIssues] = useState(null);
  const [referenceCheckLoading, setReferenceCheckLoading] = useState(false);
  const [showReferenceAlert, setShowReferenceAlert] = useState(true);
  const [showPcnDocumentation, setShowPcnDocumentation] = useState(false);

  // Get caseId and token from props or context (menuProps)
  const caseId = propCaseId || menuProps?.client?.caseId;
  const token = propToken || menuProps?.token;
  const client = menuProps?.client;

  // Auto-set bi-monthly dates when client has reportingType "דו חודשי"
  useEffect(() => {
    if (!open || !client || client.reportingType !== 'דו חודשי') {
      return;
    }

    const now = dayjs();
    const currentMonth = now.month(); // 0-11
    const currentYear = now.year();

    // Determine which bi-monthly period the current month belongs to
    let startMonth, endMonth;
    
    if (currentMonth <= 1) {
      // Jan-Feb (months 0-1)
      startMonth = 0;
      endMonth = 1;
    } else if (currentMonth <= 3) {
      // Mar-Apr (months 2-3)
      startMonth = 2;
      endMonth = 3;
    } else if (currentMonth <= 5) {
      // May-Jun (months 4-5)
      startMonth = 4;
      endMonth = 5;
    } else if (currentMonth <= 7) {
      // Jul-Aug (months 6-7)
      startMonth = 6;
      endMonth = 7;
    } else if (currentMonth <= 9) {
      // Sep-Oct (months 8-9)
      startMonth = 8;
      endMonth = 9;
    } else {
      // Nov-Dec (months 10-11)
      startMonth = 10;
      endMonth = 11;
    }

    // Set startDate to first day of start month (UTC)
    const biStartDate = dayjs.utc(new Date(Date.UTC(currentYear, startMonth, 1)));
    // Set endDate to last day of end month (UTC)
    // Using endMonth + 1 with day 0 gives us the last day of endMonth
    const biEndDate = dayjs.utc(new Date(Date.UTC(currentYear, endMonth + 1, 0)));

    // Set dates and enable date range mode
    setIsDateRange(true);
    setStartDate(biStartDate);
    setEndDate(biEndDate);
  }, [open, client, setIsDateRange, setStartDate, setEndDate]);

  // Check reference continuity when dates change
  useEffect(() => {
    if (!open || !caseId || !token) {
      // Clear issues if modal is closed or required props are missing
      if (!open) {
        setReferenceContinuityIssues(null);
      }
      return;
    }

    const checkReferenceContinuity = async () => {
      setReferenceCheckLoading(true);
      try {
        let referenceDate;
        
        if (isDateRange) {
          referenceDate = startDate || endDate;
        } else {
          referenceDate = date;
        }

        if (!referenceDate) {
          setReferenceContinuityIssues(null);
          setReferenceCheckLoading(false);
          return;
        }

        // Check reference sequence across the full year, not the selected period
        const year = dayjs(referenceDate).year();
        const startDateToCheck = dayjs.utc(new Date(Date.UTC(year, 0, 1))).toDate();
        const endDateToCheck = dayjs.utc(new Date(Date.UTC(year, 11, 31))).toDate();

        const result = await DocumentService.CheckReferenceContinuity(
          caseId,
          startDateToCheck,
          endDateToCheck,
          token
        );

        setReferenceContinuityIssues(result);
      } catch (error) {
        console.error('Error checking reference continuity:', error);
        setReferenceContinuityIssues(null);
      } finally {
        setReferenceCheckLoading(false);
      }
    };

    // Small delay to avoid checking on every keystroke
    const timeoutId = setTimeout(checkReferenceContinuity, 500);
    return () => clearTimeout(timeoutId);
  }, [open, caseId, token, isDateRange, date, startDate, endDate]);

  // Persist selected dates to localStorage and restore on open
  const STORAGE_KEY = 'pcnDate';

  useEffect(() => {
    if (!open) return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (!saved) return;
      if (saved.isDateRange != null) setIsDateRange(saved.isDateRange);
      if (saved.isDateRange) {
        if (saved.startDate) setStartDate(dayjs(saved.startDate));
        if (saved.endDate) setEndDate(dayjs(saved.endDate));
      } else {
        if (saved.date) setDate(dayjs(saved.date));
      }
    } catch { /* ignore corrupt data */ }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const payload = isDateRange
      ? { isDateRange: true, startDate: startDate?.toISOString?.() ?? startDate, endDate: endDate?.toISOString?.() ?? endDate }
      : { isDateRange: false, date: date?.toISOString?.() ?? date };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }, [open, isDateRange, date, startDate, endDate]);

  // Reset alert visibility when modal opens
  useEffect(() => {
    if (open) {
      setShowReferenceAlert(true);
    }
  }, [open]);
  return (
    <>
      <PCNDocumentationModal
        open={showPcnDocumentation}
        onClose={() => setShowPcnDocumentation(false)}
        direction={direction}
      />
      
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="pcn-modal-title"
        aria-describedby="pcn-modal-description"
        sx={{ zIndex: 1300 }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 300,
            maxWidth: 400,
            width: '90%',
            backgroundColor: 'white',
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: 'none',
          }}
          className={`modal-container ${direction}`}
        >
          {/* ───── Title with Documentation Button ─────────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <h2
              id="pcn-modal-title"
              style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 600,
                color: 'rgba(0,0,0,.9)',
              }}
            >
              PCN
            </h2>
            <Button
              startIcon={<InfoIcon />}
              onClick={() => setShowPcnDocumentation(true)}
              sx={{
                textTransform: 'none',
                fontSize: '14px'
              }}
            >
              {t('PCNDocumentation')}
            </Button>
          </Box>

        {/* ───── Reference Continuity Alert ───────────────────────── */}
        {referenceContinuityIssues && !referenceContinuityIssues.isContinuous && (
          <>
            {!showReferenceAlert && (
              <Alert 
                severity="warning" 
                sx={{ mb: 2, cursor: 'pointer' }}
                onClick={() => setShowReferenceAlert(true)}
              >
                <AlertTitle>{t('ReferenceContinuityIssues')}</AlertTitle>
                <div style={{ fontSize: '14px' }}>
                  {referenceContinuityIssues.issues?.length || 0} {t('Issues')} - {t('ClickToExpand') || 'Click to view details'}
                </div>
              </Alert>
            )}
            <Collapse in={showReferenceAlert}>
              <Alert 
                severity="warning" 
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => setShowReferenceAlert(false)}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
              <AlertTitle>{t('ReferenceContinuityIssues')}</AlertTitle>
              <div style={{ 
                direction: direction, 
                textAlign: direction === 'rtl' ? 'right' : 'left',
                marginTop: '8px'
              }}>
                <p style={{ marginBottom: '8px' }}>{t('ReferenceContinuityWarning')}</p>
                <div style={{
                  background: '#fff3cd',
                  padding: '10px',
                  borderRadius: '5px',
                  marginTop: '10px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  direction: direction,
                  textAlign: direction === 'rtl' ? 'right' : 'left',
                  fontSize: '14px'
                }}>
                  <strong>{t('IssuesFound')}</strong>
                  <ul style={{ marginTop: '8px', paddingRight: direction === 'rtl' ? '20px' : '0', paddingLeft: direction === 'ltr' ? '20px' : '0' }}>
                    {referenceContinuityIssues.issues?.map((issue, index) => {
                      let translatedDescription = issue.description;
                      if (issue.issueType === 'Duplicate') {
                        translatedDescription = t('Duplicate reference number') + ': ' + issue.actualReference;
                      } else if (issue.issueType === 'Gap') {
                        translatedDescription = t('Missing reference number') + ': ' + issue.expectedReference;
                      } else if (issue.issueType === 'Invalid') {
                        translatedDescription = t('Invalid reference format') + ': \'' + issue.actualReference + '\'';
                      }
                      return <li key={index} style={{ marginBottom: '4px' }}>{translatedDescription}</li>;
                    })}
                  </ul>
                </div>
                {viewDocumentDetails && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      onClose();
                      viewDocumentDetails('Incomes');
                    }}
                    sx={{ mt: 1 }}
                  >
                    {t('ViewIncomes')}
                  </Button>
                )}
              </div>
            </Alert>
          </Collapse>
          </>
        )}

        {/* ───── Dates ─────────────────────────────────────────────── */}
        <Box className={`modal-container ${direction}`}>
          <h2 id="date-modal-title">{t('SelectDateRange')}</h2>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>{t('SelectDateOption')}</FormLabel>
            <RadioGroup
              row
              value={isDateRange ? 'dateRange' : 'singleDate'}
              onChange={(e) => setIsDateRange(e.target.value === 'dateRange')}
            >
              <FormControlLabel
                value="singleDate"
                control={<Radio />}
                label={t('SingleDate')}
              />
              <FormControlLabel
                value="dateRange"
                control={<Radio />}
                label={t('DateRange')}
              />
            </RadioGroup>
          </FormControl>

          {isDateRange ? (
            <div className="date-picker-container">
              <CustomMonthDatePicker
                label={t('StartDate')}
                date={startDate}
                onDateChange={setStartDate}
                height="55px"
              />
              <CustomMonthDatePicker
                label={t('EndDate')}
                date={endDate}
                onDateChange={setEndDate}
                height="55px"
              />
            </div>
          ) : (
            <div className="date-picker-container">
              <CustomMonthDatePicker
                label={t('date')}
                date={date}
                onDateChange={setDate}
                height="55px"
              />
            </div>
          )}
        </Box>

        {/* ───── Storno include/exclude ─────────────────────────────── */}
        {typeof pcnIncludeStornoChecks !== 'undefined' && (
          <FormControl sx={{ mb: 2 }}>
            <FormLabel>{t('SelectStornoOption')}</FormLabel>
            <RadioGroup
              row
              value={
                pcnIncludeStornoChecks === null
                  ? ''
                  : String(pcnIncludeStornoChecks)
              }
              onChange={(e) =>
                setPcnIncludeStornoChecks(e.target.value === 'true')
              }
            >
              <FormControlLabel
                value="false"
                control={<Radio />}
                label={t('ExcludingStorno_Checks')}
              />
              <FormControlLabel
                value="true"
                control={<Radio />}
                label={t('IncludingStorno_Checks')}
              />
            </RadioGroup>
          </FormControl>
        )}

        {/* ───── Action buttons ─────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button
            variant="outlined"
            disabled={pcnChecksLoading}
            onClick={() => {
              // Call handlePCNChecks with current modal dates
              if (isDateRange) {
                handlePCNChecks(startDate?.toDate?.() || startDate, endDate?.toDate?.() || endDate);
              } else {
                handlePCNChecks(date?.toDate?.() || date, date?.toDate?.() || date);
              }
            }}
            sx={{
              minWidth: 120,
              borderRadius: 1,
              borderColor: '#304FFF',
              color: '#304FFF',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(48,79,255,.04)',
              },
            }}
          >
            {pcnChecksLoading ? t('Checking') : t('Checkss')}
          </Button>

          <Button
            variant="contained"
            disabled={pcnIncludeStornoChecks === null}
            onClick={() => {
              // Call handlePCNDownload with current modal dates
              if (isDateRange) {
                handlePCNDownload(startDate?.toDate?.() || startDate, endDate?.toDate?.() || endDate);
              } else {
                handlePCNDownload(date?.toDate?.() || date, date?.toDate?.() || date);
              }
            }}
            sx={{
              minWidth: 120,
              borderRadius: 1,
              backgroundColor:
                pcnIncludeStornoChecks === null ? 'gray' : '#304FFF',
              '&:hover': {
                backgroundColor:
                  pcnIncludeStornoChecks === null ? 'gray' : '#1565c0',
              },
            }}
          >
            {t('Download')}
          </Button>
        </Box>
      </Box>
    </Modal>
    </>
  );
}
